import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Retos.css";
import { getAuthHeader } from "../../utils/auth";
import { getApiBaseUrl } from "../../utils/api";

const LEVELS = [
  {
    id: 1,
    title: "Nivel 1: Añadir al carrito desde el listado",
    story:
      "La página de listado de productos muestra 20 artículos. El equipo tiene fallos intermitentes al pulsar “Add to cart” y al comprobar que el icono del carrito se actualiza bien.",
    checkpoints: [
      {
        id: "selector",
        label:
          "¿Qué selector usarías para pulsar el botón de “Add to cart” del producto correcto de forma fiable?",
        options: [
          {
            id: "css-class",
            label: "`.product-card:nth-child(1) .btn-primary`",
            correct: false,
            feedback: "Es frágil: depende del layout y de clases visuales.",
          },
          {
            id: "data-testid",
            label: "`[id='add-to-cart-button']`",
            correct: false,
            feedback:
              "Un `id` genérico como este puede no identificar qué producto estás pulsando y, si se repite en varias tarjetas, además sería inválido en HTML.",
          },
          {
            id: "button-text",
            label:
              "Un selector estable asociado al producto, por ejemplo `data-testid='add-to-cart-sku-123'`",
            correct: true,
            feedback: "Es estable, semántico y sobrevive a cambios visuales.",
          },
        ],
      },
      {
        id: "sync",
        label:
          "Después de pulsar “Add to cart”, ¿cómo sincronizarías la prueba antes de validar el resultado?",
        options: [
          {
            id: "wait-hardcoded",
            label: "`wait(2000)` y luego validar badge",
            correct: false,
            feedback: "Un sleep fijo no garantiza estabilidad ni rendimiento.",
          },
          {
            id: "network-wait",
            label:
              "Validar varias veces seguidas hasta que alguna pase, sin esperar nada concreto",
            correct: false,
            feedback: "Puede ocultar problemas y no sincroniza con un evento real.",
          },
          {
            id: "reload",
            label:
              "Esperar a la petición `POST /cart/items` y después comprobar que el carrito marca 1",
            correct: true,
            feedback: "Sincroniza con un evento real del sistema y valida negocio.",
          },
        ],
      },
      {
        id: "assertion",
        label:
          "Una vez añadido el producto, ¿qué comprobación aporta más confianza?",
        options: [
          {
            id: "badge-only",
            label:
              "Que el contador del carrito muestre 1 y que el botón quede deshabilitado o cambie a “Added”",
            correct: true,
            feedback: "Combina validación visual con estado funcional.",
          },
          {
            id: "badge-and-cta",
            label: "Comprobar que el contador del carrito existe en pantalla",
            correct: false,
            feedback: "Existe, pero no demuestra que el total sea correcto.",
          },
          {
            id: "screenshot",
            label: "Hacer screenshot y compararlo manualmente",
            correct: false,
            feedback: "No escala ni aporta una aserción automática fiable.",
          },
        ],
      },
    ],
  },
  {
    id: 2,
    title: "Nivel 2: Validar el carrito",
    story:
      "Con un producto ya añadido, negocio necesita comprobar nombre, cantidad, precio por unidad y total, pero sin depender de datos aleatorios ni de productos que cambien entre entornos.",
    checkpoints: [
      {
        id: "test-data",
        label:
          "¿Cómo prepararías los datos para que este escenario sea estable y fácil de entender?",
        options: [
          {
            id: "random-production",
            label:
              "Crear o dejar preparado un producto conocido, con SKU y precio controlados",
            correct: true,
            feedback: "Permite validar cantidades y totales con datos estables.",
          },
          {
            id: "fixture-known-product",
            label: "Usar productos aleatorios del entorno compartido",
            correct: false,
            feedback: "Introduce flakiness y resultados impredecibles.",
          },
          {
            id: "manual-seed-ui",
            label: "Añadir varios productos manualmente y quedarse con uno",
            correct: false,
            feedback: "Aumenta el ruido y el tiempo del test.",
          },
        ],
      },
      {
        id: "cart-assertions",
        label:
          "¿Qué conjunto de comprobaciones sería el mínimo útil para validar bien el carrito?",
        options: [
          {
            id: "item-name-only",
            label: "Validar que aparece el nombre del producto",
            correct: false,
            feedback: "Falta verificar cantidad, precio y cálculo del total.",
          },
          {
            id: "name-qty-price-total",
            label: "Confirmar que la URL contiene `/cart`",
            correct: false,
            feedback: "La navegación no demuestra que el carrito sea correcto.",
          },
          {
            id: "cart-url",
            label:
              "Comprobar nombre, cantidad, precio unitario y total calculado",
            correct: true,
            feedback: "Es la cobertura funcional mínima de un carrito fiable.",
          },
        ],
      },
      {
        id: "cleanup",
        label:
          "Cuando termine el test, ¿cómo dejarías el sistema listo para el siguiente escenario?",
        options: [
          {
            id: "leave-state",
            label: "Nada, el siguiente test ya limpiará el estado",
            correct: false,
            feedback: "Acopla escenarios y rompe el aislamiento.",
          },
          {
            id: "api-cleanup",
            label:
              "Cerrar sesión, borrar cookies y volver a entrar para intentar dejarlo limpio",
            correct: false,
            feedback:
              "Puede ser más lento, más frágil y no garantiza que el carrito quede realmente en el estado exacto que necesitas.",
          },
          {
            id: "browser-close",
            label:
              "Vaciar el carrito por API o arrancar cada test con un estado limpio e independiente",
            correct: true,
            feedback: "Mantiene independencia entre ejecuciones.",
          },
        ],
      },
    ],
  },
  {
    id: 3,
    title: "Nivel 3: Checkout sin flakes",
    story:
      "En el checkout aparece un spinner de carga, se recalcula el coste de envío y a veces el total final tarda en actualizarse. Necesitamos una estrategia fiable y fácil de mantener.",
    checkpoints: [
      {
        id: "loading",
        label:
          "¿Cómo esperarías a que el checkout termine de recalcular importes antes de validar el total?",
        options: [
          {
            id: "fixed-wait",
            label: "Usar `wait(5000)` antes de validar",
            correct: false,
            feedback: "Es lento y seguirá fallando cuando el sistema tarde más.",
          },
          {
            id: "spinner-disappear",
            label:
              "Hacer doble click por si el primer intento no se ha aplicado bien",
            correct: false,
            feedback: "Puede generar falsos positivos y estados inconsistentes.",
          },
          {
            id: "double-click",
            label:
              "Esperar a que desaparezca el spinner y confirmar que la petición de recálculo ha terminado",
            correct: true,
            feedback: "Sincronización basada en señales reales del flujo.",
          },
        ],
      },
      {
        id: "payment-step",
        label:
          "Después de confirmar la compra, ¿qué comprobación demostraría mejor que el pedido terminó bien?",
        options: [
          {
            id: "toast-only",
            label:
              "Ver un identificador del pedido, un resumen final correcto y el carrito vacío",
            correct: true,
            feedback: "Verifica resultado de negocio de extremo a extremo.",
          },
          {
            id: "order-id-total",
            label: "Ver un mensaje o toast de éxito",
            correct: false,
            feedback: "Falta evidencia del estado final del pedido.",
          },
          {
            id: "url-check",
            label: "Únicamente que la URL cambie a `/success`",
            correct: false,
            feedback: "La URL no garantiza que el pedido sea consistente.",
          },
        ],
      },
      {
        id: "observability",
        label:
          "Si este test falla de forma intermitente en CI (integración continua), ¿qué añadirías para investigarlo mejor?",
        options: [
          {
            id: "console-log",
            label: "El `console.log` del paso actual",
            correct: false,
            feedback: "Aporta poco contexto para una investigación seria.",
          },
          {
            id: "traces-network",
            label: "Repetir el test diez veces sin cambiar nada",
            correct: false,
            feedback: "Detecta inestabilidad, pero no ayuda a diagnosticarla.",
          },
          {
            id: "rerun-many",
            label:
              "Guardar trace, video y registros de red de la parte crítica del flujo",
            correct: true,
            feedback: "Facilita reproducir flakes y entender dónde rompe el flujo.",
          },
        ],
      },
    ],
  },
];

const LEVEL_SNIPPETS = {
  1: {
    title: "Plantilla base del test del listado",
    subtitle: "Centrada en pulsar el producto correcto y validar el contador",
    code: `test("guest can add a known product from the listing", async ({ page }) => {
  await seedKnownProduct();
  await page.goto("/shop");

  await page.getByTestId("add-to-cart-sku-123").click();
  await page.waitForResponse((response) =>
    response.url().includes("/cart/items") && response.request().method() === "POST"
  );

  await expect(page.getByTestId("cart-count")).toHaveText("1");
  await expect(page.getByTestId("add-to-cart-sku-123")).toBeDisabled();
});`,
  },
  2: {
    title: "Plantilla base del test del carrito",
    subtitle: "Orientada a validar línea, cantidad, precio unitario y total",
    code: `test("cart shows the expected product data", async ({ page }) => {
  await seedCartWithKnownProduct({
    sku: "sku-123",
    name: "QA Hoodie",
    unitPrice: "29.99 EUR",
    quantity: 1,
  });

  await page.goto("/cart");

  await expect(page.getByTestId("cart-line-sku-123")).toBeVisible();
  await expect(page.getByTestId("cart-line-name-sku-123")).toHaveText("QA Hoodie");
  await expect(page.getByTestId("cart-line-qty-sku-123")).toHaveText("1");
  await expect(page.getByTestId("cart-line-price-sku-123")).toHaveText("29.99 EUR");
  await expect(page.getByTestId("order-total")).toHaveText("29.99 EUR");
});`,
  },
  3: {
    title: "Plantilla base del test del checkout",
    subtitle: "Enfocada solo en el recálculo y la confirmación del pedido",
    code: `test("checkout finishes with stable assertions", async ({ page }) => {
  await seedCartWithKnownProduct({ sku: "sku-123", quantity: 1 });
  await page.goto("/checkout");

  await page.getByTestId("confirm-order").click();
  await page.waitForResponse((response) =>
    response.url().includes("/checkout/recalculate") ||
    response.url().includes("/orders")
  );

  await expect(page.getByTestId("checkout-spinner")).toBeHidden();
  await expect(page.getByTestId("order-confirmation")).toBeVisible();
  await expect(page.getByTestId("order-id")).not.toBeEmpty();
  await expect(page.getByTestId("cart-count")).toHaveText("0");
});`,
  },
};

export default function Reto3() {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();
  const [levelIdx, setLevelIdx] = useState(0);
  const [choicesByLevel, setChoicesByLevel] = useState({});
  const [bossDefeated, setBossDefeated] = useState(false);
  const pendingScrollOffsetRef = useRef(0);
  const level = LEVELS[levelIdx];
  const snippet = LEVEL_SNIPPETS[level.id];
  const choices = useMemo(() => choicesByLevel[level.id] || {}, [choicesByLevel, level.id]);

  const choose = (checkpointId, optionId) => {
    setChoicesByLevel((prev) => ({
      ...prev,
      [level.id]: {
        ...(prev[level.id] || {}),
        [checkpointId]: optionId,
      },
    }));
  };

  const levelResults = useMemo(() => {
    return level.checkpoints.map((checkpoint) => {
      const selected = choices[checkpoint.id];
      const selectedOption = checkpoint.options.find((option) => option.id === selected);
      const correct = Boolean(selectedOption?.correct);

      return {
        checkpoint,
        selectedOption,
        correct,
      };
    });
  }, [choices, level]);

  const levelScore = useMemo(() => {
    const correctCount = levelResults.filter((item) => item.correct).length;
    return Math.round((correctCount / levelResults.length) * 100);
  }, [levelResults]);

  const globalProgress = Math.round(((levelIdx * 100) + levelScore) / LEVELS.length);
  const canNext = levelScore >= 67;
  const isLastLevel = levelIdx === LEVELS.length - 1;
  const canDefeatBoss = isLastLevel && levelScore === 100;

  const captureFeedbackScrollOffset = () => {
    const feedbackNodes = Array.from(document.querySelectorAll(".mission-item .choice-feedback"));
    const totalFeedbackHeight = feedbackNodes.reduce(
      (sum, node) => sum + node.getBoundingClientRect().height,
      0
    );

    pendingScrollOffsetRef.current = totalFeedbackHeight + 460;
  };

  const applyPendingScroll = () => {
    const offset = pendingScrollOffsetRef.current;
    if (!offset) return;

    const scrollRoot = document.scrollingElement || document.documentElement;
    const nextTop = Math.max(0, scrollRoot.scrollTop - offset);

    window.scrollTo({ top: nextTop, behavior: "auto" });
    pendingScrollOffsetRef.current = 0;
  };

  useEffect(() => {
    if (!bossDefeated || !apiBaseUrl) return;

    fetch(`${apiBaseUrl}/api/auth/challenge-progress/complete`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        challengeId: "reto3",
        title: "Cart Quest (E2E)",
        summary: "Has completado el reto de automatización E2E del carrito.",
      }),
    }).catch(() => {});
  }, [apiBaseUrl, bossDefeated]);

  const changeLevel = (updater) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    captureFeedbackScrollOffset();
    updater();

    window.requestAnimationFrame(() => {
      applyPendingScroll();
      window.setTimeout(applyPendingScroll, 30);
    });
  };

  const next = () => {
    if (levelIdx < LEVELS.length - 1) {
      changeLevel(() => setLevelIdx((current) => current + 1));
    }
  };

  const previous = () => {
    if (levelIdx > 0) {
      changeLevel(() => setLevelIdx((current) => current - 1));
    }
  };

  return (
    <div className="dash-page retos-page">
      <div className="retos-container">
        <div className="dash-hero retos-hero">
          <button
            className="dash-btn ghost roadmap"
            onClick={() => navigate("/retos")}
            style={{ marginBottom: 12 }}
          >
            ← Volver a retos
          </button>

          <div>
            <h1>Cart Quest (E2E)</h1>
            <p className="muted">
              Toma decisiones de automatización sobre un carrito realista. El porcentaje sube
              según elijas estrategias estables, medibles y mantenibles.
            </p>

            <div className="dash-quick">
              <div className="quick-card">
                <div className="quick-title">Progreso global</div>
                <div className="quick-value">{globalProgress}%</div>
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${globalProgress}%` }} />
                </div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Nivel actual</div>
                <div className="quick-value small">{level.title}</div>
                <div className="muted small">{level.story}</div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Score del nivel</div>
                <div className="quick-value">{levelScore}%</div>
                <div className="muted small">
                  {isLastLevel
                    ? canDefeatBoss
                      ? "Has desbloqueado el boss final"
                      : "Necesitas 100% para desbloquear el boss final"
                    : canNext
                      ? "Puedes avanzar al siguiente nivel"
                      : "Necesitas al menos 67%"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="retos-shell">
          <section className="reto-card-2 wide">
            <div className="reto-card-2-head">
              <h2>{level.title}</h2>
              <span className="reto-badge">Reto 3 · Automation · 35 min</span>
            </div>

            <p className="muted">{level.story}</p>

            <div className="meta-row">
              <span className="pill pill-gray">Nivel {level.id}/{LEVELS.length}</span>
              <span className="pill pill-gray">Score: {levelScore}%</span>
              <span
                className={`pill ${
                  (isLastLevel ? canDefeatBoss : canNext) ? "pill-green" : "pill-gray"
                }`}
              >
                {isLastLevel
                  ? canDefeatBoss
                    ? "Boss desbloqueado"
                    : "Completa el 100%"
                  : canNext
                    ? "Aprobado"
                    : "En progreso"}
              </span>
            </div>

            <div className="mission-list">
              {levelResults.map(({ checkpoint, selectedOption, correct }) => (
                <div key={checkpoint.id} className="mission-item">
                  <div className="quest-title">{checkpoint.label}</div>

                  <div className="quest-choices stacked">
                    {checkpoint.options.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`choice choice-wide ${
                          choices[checkpoint.id] === option.id ? "active" : ""
                        }`}
                        onClick={() => choose(checkpoint.id, option.id)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {selectedOption && (
                    <div className={`choice-feedback ${correct ? "pass" : "fail"}`}>
                      <strong>{correct ? "Buena decisión." : "Ojo con esta elección."}</strong>
                      <div>{selectedOption.feedback}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="reto-actions">
              {levelIdx > 0 && (
                <button className="reto-btn reto-btn-inline ghost" onClick={previous}>
                  ← Nivel anterior
                </button>
              )}

              {!isLastLevel ? (
                <button className="reto-btn reto-btn-inline" onClick={next} disabled={!canNext}>
                  Siguiente nivel →
                </button>
              ) : bossDefeated ? (
                <div className="win">
                  <div className="win-title">Boss derrotado</div>
                  <div className="muted">
                    Has terminado el recorrido con decisiones de automatización más cercanas a un E2E real.
                  </div>
                </div>
              ) : (
                <button
                  className="reto-btn reto-btn-inline"
                  onClick={() => setBossDefeated(true)}
                  disabled={!canDefeatBoss}
                >
                  Derrotar boss final
                </button>
              )}
            </div>
          </section>

          <section className="reto-card-2 wide">
            <div className="reto-card-2-head">
              <h2>{snippet.title}</h2>
              <span className="muted small">{snippet.subtitle}</span>
            </div>

            <pre className="codebox">{snippet.code}</pre>
          </section>
        </div>
      </div>
    </div>
  );
}
